import type { StatusTone } from "@/components/ui";
import type {
  Priority,
  SupportConversationAction,
  SupportDeliveryStatus,
  SupportTicketAction,
  SupportTicketStatus,
} from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

export function ticketStatusTone(status: SupportTicketStatus): StatusTone {
  switch (status) {
    case "new":
    case "reopened":
      return "warning";
    case "open":
    case "assigned":
      return "info";
    case "pending_customer":
    case "pending_internal_resolution":
    case "escalated":
      return "warning";
    case "resolved":
      return "success";
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function priorityTone(priority: Priority): StatusTone {
  switch (priority) {
    case "low":
    case "normal":
      return "info";
    case "high":
      return "warning";
    case "urgent":
    case "critical":
      return "danger";
    default:
      return "neutral";
  }
}

export function deliveryStatusTone(status: SupportDeliveryStatus): StatusTone {
  switch (status) {
    case "pending":
      return "warning";
    case "sent":
    case "delivered":
      return "success";
    case "failed":
      return "danger";
    case "suppressed":
      return "neutral";
    default:
      return "neutral";
  }
}

export const TICKET_ACTIONS_FROM: Record<
  SupportTicketAction,
  readonly SupportTicketStatus[]
> = {
  open: ["new", "reopened"],
  assign: [
    "new",
    "open",
    "assigned",
    "pending_customer",
    "pending_internal_resolution",
    "escalated",
    "reopened",
  ],
  pending_customer: [
    "open",
    "assigned",
    "pending_internal_resolution",
    "escalated",
    "reopened",
  ],
  pending_internal: [
    "open",
    "assigned",
    "pending_customer",
    "escalated",
    "reopened",
  ],
  escalate: [
    "open",
    "assigned",
    "pending_customer",
    "pending_internal_resolution",
    "reopened",
  ],
  resolve: [
    "open",
    "assigned",
    "pending_customer",
    "pending_internal_resolution",
    "escalated",
    "reopened",
  ],
  close: ["resolved", "open", "assigned", "escalated"],
  reopen: ["closed", "resolved"],
};

export const CONVERSATION_ACTIONS_FROM: Record<
  SupportConversationAction,
  readonly SupportDeliveryStatus[]
> = {
  mark_sent: ["pending"],
  mark_delivered: ["sent"],
  mark_failed: ["pending", "sent"],
  suppress: ["pending", "sent", "delivered", "failed"],
};

export function allowedTicketActions(
  status: SupportTicketStatus,
): SupportTicketAction[] {
  return (Object.keys(TICKET_ACTIONS_FROM) as SupportTicketAction[]).filter(
    (action) => TICKET_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedConversationActions(
  status: SupportDeliveryStatus,
): SupportConversationAction[] {
  return (
    Object.keys(CONVERSATION_ACTIONS_FROM) as SupportConversationAction[]
  ).filter((action) => CONVERSATION_ACTIONS_FROM[action].includes(status));
}

/** Convert datetime-local (or any parseable date) to UTC ISO with offset. */
export function toIsoUtc(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toISOString();
}
